import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetClientContextQuery,
  useGetClientGapsQuery,
  useSubmitClientGapMutation,
} from '../../features/platform/platformApi';
import {
  useGetReferenceDirectoryQuery,
  useDeleteReferencePersonMutation,
  refName,
  refPhone,
} from '../../features/referenceDirectory/referenceDirectoryApi';

/* ------------------------------------------------------------------ *
 * Client Admin — Key people and gaps.
 * Coverage gaps (territories not fully mapped) come from the client's state
 * (GET /platform/client/gaps/) and a "request a change" POSTs to the review
 * queue. Posts-not-on-record and persons-of-interest have no backend yet (local).
 * ------------------------------------------------------------------ */

// Illustrative totals shown on the filter pills and in the lead line.
const COUNTS = { all: 91, official: 87, elected: 4, onRecord: 5, total: 96 };

interface Post {
  id: string;
  title: string;
  place: string;
  path: string;
  elected: boolean;
}
const REV = 'Revenue & Land Administration';
const OFFICIAL_POSTS: Post[] = [
  { id: 'o1', title: 'Principal Secretary, Revenue', place: 'Rajasthan', path: `${REV} › State`, elected: false },
  { id: 'o2', title: 'Divisional Commissioner', place: 'Jaipur Division', path: `${REV} › Division`, elected: false },
  { id: 'o3', title: 'District Collector', place: 'Alwar', path: `${REV} › District`, elected: false },
  { id: 'o4', title: 'District Collector', place: 'Khairthal-Tijara', path: `${REV} › District`, elected: false },
  { id: 'o5', title: 'District Collector', place: 'Kotputli-Behror', path: `${REV} › District`, elected: false },
  { id: 'o6', title: 'Sub-divisional Officer', place: 'Alwar SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o7', title: 'Sub-divisional Officer', place: 'Ramgarh SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o8', title: 'Sub-divisional Officer', place: 'Rajgarh SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o9', title: 'Sub-divisional Officer', place: 'Laxmangarh SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o10', title: 'Sub-divisional Officer', place: 'Kathumar SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o11', title: 'Sub-divisional Officer', place: 'Thanagazi SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o12', title: 'Sub-divisional Officer', place: 'Tijara SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o13', title: 'Sub-divisional Officer', place: 'Kishangarh Bas SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o14', title: 'Sub-divisional Officer', place: 'Behror SD', path: `${REV} › Sub-division`, elected: false },
  { id: 'o15', title: 'Tahsildar', place: 'Alwar', path: `${REV} › Tehsil`, elected: false },
  { id: 'o16', title: 'Tahsildar', place: 'Ramgarh', path: `${REV} › Tehsil`, elected: false },
  { id: 'o17', title: 'Tahsildar', place: 'Rajgarh', path: `${REV} › Tehsil`, elected: false },
  { id: 'o18', title: 'Tahsildar', place: 'Laxmangarh', path: `${REV} › Tehsil`, elected: false },
  { id: 'o19', title: 'Tahsildar', place: 'Kathumar', path: `${REV} › Tehsil`, elected: false },
  { id: 'o20', title: 'Tahsildar', place: 'Malakhera', path: `${REV} › Tehsil`, elected: false },
  { id: 'o21', title: 'Tahsildar', place: 'Thanagazi', path: `${REV} › Tehsil`, elected: false },
  { id: 'o22', title: 'Tahsildar', place: 'Reni', path: `${REV} › Tehsil`, elected: false },
  { id: 'o23', title: 'Tahsildar', place: 'Tijara', path: `${REV} › Tehsil`, elected: false },
  { id: 'o24', title: 'Tahsildar', place: 'Kishangarh Bas', path: `${REV} › Tehsil`, elected: false },
];
const ELECTED_POSTS: Post[] = [
  { id: 'e1', title: 'Pradhan', place: 'Rajgarh', path: 'Panchayati Raj › Panchayat Samiti', elected: true },
  { id: 'e2', title: 'Pradhan', place: 'Reni', path: 'Panchayati Raj › Panchayat Samiti', elected: true },
  { id: 'e3', title: 'Sarpanch', place: 'Bahadarpur', path: 'Panchayati Raj › Gram Panchayat', elected: true },
  { id: 'e4', title: 'Zila Pramukh', place: 'Alwar', path: 'Panchayati Raj › Zila Parishad', elected: true },
];

const covColor = (pct: number) => (pct < 70 ? 'text-[#b42318]' : 'text-[#b45309]');

export default function ClientGapsPage() {
  const navigate = useNavigate();
  // "Add who holds it" opens the create-person screen — Elected (political) or Official
  // (government-official) — with the post's designation pre-filled, returning here on save.
  const addHolder = (post: Post) => {
    const base = post.elected ? 'political' : 'government-official';
    const qs = new URLSearchParams({
      designation: post.title,
      lockDesignation: '1',
      returnTo: '/client/gaps',
    });
    navigate(`/client/people/${base}/new?${qs.toString()}`);
  };
  const { data: ctx } = useGetClientContextQuery();
  const { data: gaps = [], isLoading: gapsLoading } = useGetClientGapsQuery();
  const [submitGap] = useSubmitClientGapMutation();
  const [gapSent, setGapSent] = useState<Record<string, boolean>>({});
  const sendGap = async (g: { dept: string; level: string; message: string }) => {
    const key = `${g.dept}·${g.level}`;
    try {
      await submitGap({
        title: `${g.dept} · ${g.level} — village lists`,
        type: 'Coverage gap',
        detail: g.message,
      }).unwrap();
      setGapSent((s) => ({ ...s, [key]: true }));
    } catch {
      /* surfaced elsewhere */
    }
  };

  type Filter = 'all' | 'official' | 'elected' | 'reference';
  const [filter, setFilter] = useState<Filter>('all');

  // Persons of interest = the reference directory, scoped to the client's org (list/add/edit/delete).
  const { data: refData, isLoading: poiLoading } = useGetReferenceDirectoryQuery(
    { organization: ctx?.organization_id },
    // Refetch on return from the add/edit form (which invalidates Users, not ReferenceDirectory).
    { skip: !ctx?.organization_id, refetchOnMountOrArgChange: true },
  );
  const persons = refData?.items ?? [];
  const [deletePerson] = useDeleteReferencePersonMutation();

  const shownGaps = filter === 'elected' ? ELECTED_POSTS : OFFICIAL_POSTS;
  const totalForFilter = filter === 'reference' ? persons.length : COUNTS[filter];
  const moreCount = filter === 'reference' ? 0 : totalForFilter - shownGaps.length;
  const filterBtn = (key: Filter, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => setFilter(key)}
      className={['rounded-full px-3 py-1 text-xs font-semibold transition-colors', filter === key ? 'bg-brand text-white' : 'border border-line bg-white text-ink'].join(' ')}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col gap-[18px]">
      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">CLIENT SETUP</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Key people and gaps</h1>
        <p className="max-w-[820px] text-sm leading-6 text-ink-secondary">
          {COUNTS.onRecord} of {COUNTS.total} posts in your departments are on record from the platform.
          Fill the gaps you know about. Sharing sends the name to the platform for review; keeping it
          private means only your team sees it.
        </p>
      </section>

      {/* Posts not on record */}
      <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <h2 className="text-lg font-bold text-ink">Posts not on record</h2>
        <span className="text-[13px] text-ink-secondary">
          Officials and elected representatives. Elected posts change at elections, so record the term
          with the name.
        </span>
        <div className="flex flex-wrap gap-1.5">
          {filterBtn('all', `All (${COUNTS.all})`)}
          {filterBtn('official', `Official (${COUNTS.official})`)}
          {filterBtn('elected', `Elected (${COUNTS.elected})`)}
          {filterBtn('reference', `Reference person (${persons.length})`)}
        </div>
        {filter === 'reference' ? (
          <>
            {poiLoading && <span className="py-2 text-[13px] text-ink-secondary">Loading…</span>}
            {!poiLoading && persons.length === 0 && (
              <span className="py-2 text-[13px] text-ink-secondary">No reference persons yet.</span>
            )}
            {persons.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#f3f4f6] py-2.5">
                <span className="flex min-w-0 flex-col">
                  <b className="text-sm text-ink">{refName(p)}</b>
                  <span className="text-xs text-ink-secondary">
                    {[p.designation, p.department, refPhone(p)].filter(Boolean).join(' · ') || '—'}
                  </span>
                </span>
                <span className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/client/persons/${p.id}/edit?returnTo=/client/gaps`)}
                    className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void deletePerson(p.id)}
                    className="h-9 rounded-lg border border-[#fecaca] bg-white px-3 text-xs font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]"
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))}
          </>
        ) : (
          <>
            {shownGaps.map((g) => (
              <div key={g.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#f3f4f6] py-2.5">
                <span className="flex flex-col">
                  <b className="text-sm text-ink">
                    {g.title} · {g.place}
                  </b>
                  <span className="text-xs text-ink-secondary">
                    {g.path} · {g.elected ? 'elected' : 'official'} post
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => addHolder(g)}
                  className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
                >
                  Add who holds it
                </button>
              </div>
            ))}
            {moreCount > 0 && (
              <button type="button" className="w-fit pt-1 text-[13px] font-semibold text-brand transition-colors hover:text-brand-dark">
                + {moreCount} more
              </button>
            )}
          </>
        )}
      </section>

      {/* Territories not fully mapped — real coverage gaps for the client's state */}
      <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <h2 className="text-lg font-bold text-ink">Territories not fully mapped</h2>
        <span className="text-[13px] text-ink-secondary">The platform maps territories. If your team has the village lists, send them.</span>
        {gapsLoading && <span className="py-2 text-[13px] text-ink-secondary">Loading gaps…</span>}
        {!gapsLoading && gaps.length === 0 && (
          <span className="py-2 text-[13px] text-ink-secondary">
            Everything in {ctx?.state_name ?? 'your state'} is fully mapped — no gaps.
          </span>
        )}
        {gaps.map((g) => {
          const key = `${g.dept}·${g.level}`;
          return (
            <div key={key} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#f3f4f6] py-2">
              <span className="flex flex-col">
                <b className="text-ink">
                  {g.dept} › {g.level}
                </b>
                <span className="text-xs text-ink-secondary">
                  {g.coverage == null ? (
                    <span className="font-semibold text-[#b42318]">members not chosen yet</span>
                  ) : (
                    <span className={['font-semibold', covColor(g.coverage)].join(' ')}>{g.coverage}% mapped</span>
                  )}
                  {g.message ? ` · ${g.message}` : ''}
                </span>
              </span>
              {gapSent[key] ? (
                <span className="rounded-full bg-[#f6e7c8] px-2.5 py-0.5 text-xs font-semibold text-[#6b4a0a]">Sent to the platform</span>
              ) : (
                <button
                  type="button"
                  onClick={() => void sendGap(g)}
                  className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
                >
                  Send village lists
                </button>
              )}
            </div>
          );
        })}
      </section>

      {/* Persons of interest — the reference directory (list · add · edit · delete) */}
      <section className="flex flex-col gap-2.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-ink">Persons of interest</h2>
          <button
            type="button"
            onClick={() => navigate('/client/persons/new?returnTo=/client/gaps')}
            className="h-10 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            + Add person
          </button>
        </div>
        <span className="text-[13px] text-ink-secondary">
          People your organisation considers important: lawyers, doctors, business and community
          leaders. Private to your team.
        </span>
        {poiLoading && <span className="text-[13px] text-ink-secondary">Loading…</span>}
        {!poiLoading && persons.length === 0 && (
          <span className="text-[13px] text-ink-secondary">No one added yet.</span>
        )}
        {persons.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#f3f4f6] py-2.5">
            <span className="flex min-w-0 flex-col">
              <b className="text-sm text-ink">{refName(p)}</b>
              <span className="text-xs text-ink-secondary">
                {[p.designation, p.department, refPhone(p)].filter(Boolean).join(' · ') || '—'}
              </span>
            </span>
            <span className="flex gap-1.5">
              <button
                type="button"
                onClick={() => navigate(`/client/persons/${p.id}/edit?returnTo=/client/gaps`)}
                className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => void deletePerson(p.id)}
                className="h-9 rounded-lg border border-[#fecaca] bg-white px-3 text-xs font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]"
              >
                Delete
              </button>
            </span>
          </div>
        ))}
      </section>
    </div>
  );
}
